// The Xone Business plans: what each costs and how many companies it allows. Shared by the website
// (prices and checkout), the API (checkout and reading Stripe) and the web app (the Subscription
// screen). The database keeps its own copy of the limits in plan_company_limit() (migration 0037)
// and is the authority; a test keeps the two in step.

export type PlanId = 'starter' | 'growth' | 'scale';
export type BillingInterval = 'month' | 'year';

export interface Plan {
  id: PlanId;
  name: string;
  /** US dollars per month. A year costs ten months. */
  monthlyUsd: number;
  /** Companies the owner may have; null means no limit. */
  companyLimit: number | null;
}

export const PLANS: readonly Plan[] = [
  { id: 'starter', name: 'Starter', monthlyUsd: 19, companyLimit: 1 },
  { id: 'growth', name: 'Growth', monthlyUsd: 39, companyLimit: 3 },
  { id: 'scale', name: 'Scale', monthlyUsd: 79, companyLimit: null },
];

export const yearlyUsd = (plan: Plan) => plan.monthlyUsd * 10;

/** Stripe lookup keys, `<plan>_monthly` or `<plan>_yearly`: one active price each. */
export const PRICE_IDS = [
  'starter_monthly',
  'starter_yearly',
  'growth_monthly',
  'growth_yearly',
  'scale_monthly',
  'scale_yearly',
] as const;
export type PriceId = (typeof PRICE_IDS)[number];

export const isPriceId = (value: unknown): value is PriceId =>
  typeof value === 'string' && (PRICE_IDS as readonly string[]).includes(value);

/** The plan and interval a lookup key stands for, or null when it is not one of ours. */
export function parsePriceId(value: unknown): { plan: PlanId; interval: BillingInterval } | null {
  if (!isPriceId(value)) return null;
  const [plan, billing] = value.split('_') as [PlanId, 'monthly' | 'yearly'];
  return { plan, interval: billing === 'yearly' ? 'year' : 'month' };
}

export const planById = (id: string): Plan | undefined => PLANS.find((p) => p.id === id);

/** 60% off the first six monthly payments, for a first subscription only. */
export const INTRO_COUPON = 'intro_60_off_6_months';
